import { Fragment } from 'react';
import { FileTrigger } from 'react-aria-components';
import { useSearchParams } from 'react-router';
import { Plural, Trans } from '@lingui/react/macro';

import { MediumType } from '@/api';
import {
  FileWithError,
  FileWithMediumId,
  useFileUploader,
} from '@/components/FileUpload/useFileUploader';
import Loader from '@/components/Loader';
import Button from '@/design_system/Button';
import { getFormattedSize } from '@/design_system/FileCard/FileCard';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { ACCEPTED_FILE_TYPES } from '@/design_system/InputFile/InputFile';
import Logo from '@/design_system/Logo';
import PhotoCard from '@/design_system/PhotoCard';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import IconDone from '@/icons/Done.svg';
import IconError from '@/icons/Error.svg';
import IconFile from '@/icons/File.svg';
import IconPhoneUpload from '@/icons/PhoneUpload.svg';
import IconRefusal from '@/icons/Refusal.svg';
import { createBEMClasses } from '@/utils/classname';

import './MobileUpload.css';

const { block, element } = createBEMClasses('mobile-upload');

const DEFAULT_UPLOAD_TYPE = 'file';

type Upload = FileWithMediumId &
  FileWithError & {
    thumb: string;
    size: string;
  };

const MobileUpload = () => {
  const [searchParams] = useSearchParams();
  const searchParamsLimitedToken = searchParams.get('limitedToken');
  const requestHeaders: { 'limited-token'?: string } = {};

  if (searchParamsLimitedToken !== null) {
    requestHeaders['limited-token'] = searchParamsLimitedToken;
  }
  const { loadingOrSucceededUploads, handleUpload, failedUploads } = useFileUploader({
    uploadData: {
      type: (searchParams.get('type') as MediumType) ?? undefined,
      requestId: searchParams.get('requestId') ?? undefined,
      articleId: searchParams.get('articleId') ?? undefined,
      userId: searchParams.get('userId') ?? undefined,
      articleConditionId: searchParams.get('articleConditionId') ?? undefined,
      articleDefectId: searchParams.get('articleDefectId') ?? undefined,
      articleActionId: searchParams.get('articleActionId') ?? undefined,
      invoiceId: searchParams.get('invoiceId') ?? undefined,
    },
    onDelete: () => {},
    headers: requestHeaders,
  });

  const maxNumberOfMedia =
    searchParams.get('maxNumberOfMedia') !== null
      ? parseInt(searchParams.get('maxNumberOfMedia') ?? '')
      : null;

  const uploadType = (searchParams.get('uploadType') ??
    DEFAULT_UPLOAD_TYPE) as keyof typeof ACCEPTED_FILE_TYPES;

  const handleClickUpload = () => {
    document.getElementById('file-capture')?.click();
  };

  const loading = !!loadingOrSucceededUploads.filter(({ mediumId }) => !mediumId).length;
  const mainError = !!failedUploads.filter(({ error }) => error?.status === 403).length;
  const uploads: Upload[] = [
    ...loadingOrSucceededUploads.map(({ file, mediumId }) => ({
      mediumId,
      file,
      thumb: URL.createObjectURL(file),
      size: getFormattedSize(file.size),
    })),
    ...failedUploads.map(({ file, error }) => ({
      file,
      thumb: URL.createObjectURL(file),
      size: getFormattedSize(file.size),
      error,
    })),
  ];

  return (
    <Stack className={block()} gap={'2rem'}>
      <header>
        <Logo size="x-small" />
      </header>
      {loading && (
        <div className={element('loader-container')}>
          <Loader />
          <Paragraph size="50">
            <Trans id="mobile-upload.loading">Uploading files</Trans>
          </Paragraph>
        </div>
      )}
      {!mainError && (
        <>
          {!loading && Boolean(loadingOrSucceededUploads?.length) && (
            <div className={element('loader-container', {}, 'color-primary-700')}>
              <IconDone fontSize="2em" />
              <Paragraph size="50" className={element('loader-text')}>
                <Plural
                  id="mobile-upload.loaded"
                  value={loadingOrSucceededUploads.length}
                  one="# file successfully uploaded"
                  other="# files successfully uploaded"
                />
              </Paragraph>
            </div>
          )}
          <main>
            {!uploads?.length ? (
              <div className={element('placeholder')}>
                <div className={element('mobile-upload-icon')}>
                  <IconPhoneUpload />
                </div>
                <Paragraph size="50">
                  <Trans id="mobile-upload.prompt">Click the button below to upload files.</Trans>
                </Paragraph>
              </div>
            ) : (
              <>
                {[...uploads].map(({ thumb, file, mediumId, size, error }, fileKey) => (
                  <Fragment key={fileKey}>
                    <div className={element('upload-list-item', { error: !!error })}>
                      {ACCEPTED_FILE_TYPES.photo.includes(file.type) || !mediumId ? (
                        <PhotoCard
                          url={thumb}
                          loading={!mediumId && !error}
                          name={file.name}
                          alt={file.name}
                        />
                      ) : (
                        <div
                          className={element('upload-list-item-file-card', {}, 'text-secondary')}
                        >
                          <IconFile style={{ fontSize: '1.5rem' }} />
                        </div>
                      )}
                      <Stack
                        justifyContent="center"
                        alignItems="flex-start"
                        style={{ overflow: 'hidden' }}
                      >
                        <InlineText ellipsis style={{ maxWidth: '100%' }}>
                          {file.name}
                        </InlineText>
                        <InlineText size="200" color="disabled">
                          {size}
                        </InlineText>
                      </Stack>
                    </div>
                    {!!error && (
                      <div className={element('upload-list-item-error-label')} key={thumb}>
                        <IconError />
                        {error.message.includes('The file must be smaller than 20 MB') ? (
                          <Trans id="mobile-upload.size-error">File size exceeds 20MB limit</Trans>
                        ) : (
                          <Trans id="mobile-upload.upload-error">An error occurred</Trans>
                        )}
                      </div>
                    )}
                  </Fragment>
                ))}
              </>
            )}
          </main>
        </>
      )}
      {mainError && (
        <main>
          <div className={element('placeholder')}>
            <div className={element('mobile-upload-icon-error')}>
              <IconRefusal />
            </div>
            <Paragraph size="50">
              <Trans id="mobile-upload.error">
                This upload link is no longer valid. It may have expired due to inactivity or time
                limit.
              </Trans>
            </Paragraph>
          </div>
        </main>
      )}
      {!mainError &&
        (!uploads?.length || maxNumberOfMedia === null || uploads?.length < maxNumberOfMedia) && (
          <Footer>
            <FooterButtons variant="full">
              <FileTrigger
                onSelect={(fileList) => {
                  if (fileList) {
                    handleUpload(Array.from(fileList as Iterable<File>));
                  }
                }}
                acceptedFileTypes={ACCEPTED_FILE_TYPES[uploadType]}
                allowsMultiple={maxNumberOfMedia === null || maxNumberOfMedia > 1}
              >
                {!uploads?.length && (
                  <Button onPress={handleClickUpload}>
                    <Trans id="mobile-upload.upload-button">
                      Upload your{' '}
                      <Plural
                        value={maxNumberOfMedia === null || maxNumberOfMedia > 1 ? 2 : 1}
                        one="file"
                        other="files"
                      />
                    </Trans>
                  </Button>
                )}
                {Boolean(uploads?.length) &&
                  (maxNumberOfMedia === null || uploads?.length < maxNumberOfMedia) && (
                    <Button variant="secondary" onPress={handleClickUpload}>
                      <IconAdd />
                      <Trans id="mobile-upload.upload-more-button">Upload more files</Trans>
                    </Button>
                  )}
              </FileTrigger>
            </FooterButtons>
          </Footer>
        )}
    </Stack>
  );
};

export default MobileUpload;
